import React, { useState, useMemo, useCallback } from 'react';
import { TouchableOpacity, ScrollView, Switch, Platform } from 'react-native';
import { Text, useThemeName } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import { ChevronLeft, ChevronRight, Bell, Mail, Volume2, Clock } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { LinearGradient } from '@/components/GradientView';
import { Screen, ScreenHeader } from '@/components';
import { useIsMobileLayout } from '@/hooks/useWindowWidth';

// Types
import type { RootStackScreenProps } from '@/types/navigation';

// API
import { useMyApplications } from '@/api/hooks/useMeetingApplications';

// Utils
import { COLORS } from '@/constants/colors';

type Props = RootStackScreenProps<'MyApplicationsCalendar'>;

const DAYS_PL = ['NIEDZ', 'PON', 'WT', 'ŚR', 'CZW', 'PT', 'SOB'];
const MONTHS_PL = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
];

// Helper to get days in month
const getDaysInMonth = (month: number, year: number): number => {
  return new Date(year, month + 1, 0).getDate();
};

// Helper to get first day of month (0 = Sunday)
const getFirstDayOfMonth = (month: number, year: number): number => {
  return new Date(year, month, 1).getDay();
};


const MyApplicationsCalendarScreen: React.FC<Props> = ({ navigation: rootNavigation }) => {
  const themeName = useThemeName();
  const { t } = useTranslation();
  const isDark = themeName === 'dark';
  const isMobileLayout = useIsMobileLayout();
  
  const bgColor = isDark ? COLORS.BLACK : '#f5f5f5';

  const titleColor = isDark ? '#ffffff' : '#18181b';
  const subtitleColor = isDark ? '#a1a1aa' : '#71717a';
  const cardBg = isDark ? '#1C1C1C' : COLORS.WHITE;
  const softBorder = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)';

  const gradientColors = isDark
    ? [COLORS.DARK_GRADIENT_START, COLORS.DARK_GRADIENT_END]
    : [COLORS.LIGHT_GRADIENT_START, COLORS.LIGHT_GRADIENT_END];

  // Calendar state
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Notification settings state
  // TODO: API - Load from GET /api/users/me/notification-settings
  const [pushNotifications, setPushNotifications] = useState(true);
  const [emailReminders, setEmailReminders] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoReminders, setAutoReminders] = useState(true);

  // Fetch user's applications
  const { data: applications = [] } = useMyApplications();

  // For demo purposes, we'll create some mock meeting dates
  // In production, this would come from fetching announcements for each application
  const meetingDates = useMemo(() => {
    // Mock dates - in production, fetch from announcements
    const today = new Date();
    return [
      new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1), // Tomorrow
      new Date(today.getFullYear(), today.getMonth(), 15),
      new Date(today.getFullYear(), today.getMonth(), 22),
    ];
  }, []);

  // Find upcoming meeting
  const upcomingMeeting = useMemo(() => {
    const now = new Date();
    const upcoming = meetingDates
      .filter(d => d > now)
      .sort((a, b) => a.getTime() - b.getTime())[0];
    
    if (!upcoming) return null;
    
    // Mock meeting info - in production, fetch from announcement
    return {
      date: upcoming,
      title: 'Kawa z Kasią', // Would come from announcement.additionalInfo
      time: '14:00', // Would come from announcement.meetingTime
      reminder: '14:00', // Would come from reminder settings
    };
  }, [meetingDates]);

  // Calendar grid
  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(calendarMonth, calendarYear);
    const firstDay = getFirstDayOfMonth(calendarMonth, calendarYear);
    const days: (number | null)[] = [];

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Days of month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  }, [calendarMonth, calendarYear]);

  // Check if a day has a meeting
  const hasMeeting = useCallback((day: number) => {
    return meetingDates.some(
      d => d.getDate() === day && 
           d.getMonth() === calendarMonth && 
           d.getFullYear() === calendarYear
    );
  }, [meetingDates, calendarMonth, calendarYear]);

  // Check if day is today
  const isToday = useCallback((day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      calendarMonth === today.getMonth() &&
      calendarYear === today.getFullYear()
    );
  }, [calendarMonth, calendarYear]);

  // Navigation handlers
  const goToPrevMonth = useCallback(() => {
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear(calendarYear - 1);
    } else {
      setCalendarMonth(calendarMonth - 1);
    }
  }, [calendarMonth, calendarYear]);

  const goToNextMonth = useCallback(() => {
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear(calendarYear + 1);
    } else {
      setCalendarMonth(calendarMonth + 1);
    }
  }, [calendarMonth, calendarYear]);

  // Format upcoming meeting date
  const formatUpcomingDate = (date: Date): string => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === tomorrow.toDateString()) {
      return t('screens.myApplicationsCalendar.tomorrow', 'Jutro');
    }

    const day = date.getDate();
    const month = MONTHS_PL[date.getMonth()];
    return `${day} ${month}`;
  };

  // Handle notification toggle
  // TODO: API - Save to PUT /api/users/me/notification-settings
  const handleToggle = (setting: string, value: boolean) => {
    switch (setting) {
      case 'push':
        setPushNotifications(value);
        break;
      case 'email':
        setEmailReminders(value);
        break;
      case 'sound':
        setSoundEnabled(value);
        break;
      case 'auto':
        setAutoReminders(value);
        break;
    }
    console.log('TODO: Save notification setting:', setting, value);
  };

  const renderSettingRow = (
    icon: React.ReactNode,
    title: string,
    subtitle: string,
    value: boolean,
    onValueChange: (val: boolean) => void
  ) => (
    <XStack
      paddingVertical="$3"
      alignItems="center"
      borderBottomWidth={1}
      borderBottomColor={softBorder}
    >
      <YStack
        width={40}
        height={40}
        borderRadius={20}
        backgroundColor={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}
        alignItems="center"
        justifyContent="center"
        marginRight="$3"
      >
        {icon}
      </YStack>
      <YStack flex={1}>
        <Text fontSize={14} fontWeight="500" fontFamily="Poppins" color={titleColor}>
          {title}
        </Text>
        <Text fontSize={12} fontFamily="Poppins" color={subtitleColor}>
          {subtitle}
        </Text>
      </YStack>
      <Switch
        value={value}
        onValueChange={onValueChange}
        trackColor={{ false: '#767577', true: COLORS.PRIMARY }}
        thumbColor={value ? '#ffffff' : '#f4f3f4'}
      />
    </XStack>
  );

  return (
    <Screen edges={['top']}>
      <YStack flex={1} backgroundColor={bgColor}>
        {/* Header */}
        {(Platform.OS === 'web' && !isMobileLayout) ? (
          <ScreenHeader
            title={t('screens.myApplicationsCalendar.title', 'Kalendarz')}
            onBackPress={() => rootNavigation.goBack()}
          />
        ) : (
          <LinearGradient
            colors={gradientColors}
            style={{ paddingTop: 48, paddingBottom: 12 }}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
          >
            <YStack paddingHorizontal="$4" paddingBottom="$3">
              <XStack alignItems="center" space="$3">
                <TouchableOpacity onPress={() => rootNavigation.goBack()}>
                  <ChevronLeft size={28} color={titleColor} />
                </TouchableOpacity>
                <Text
                  fontSize={28}
                  fontWeight="bold"
                  fontFamily="Poppins"
                  color={titleColor}
                >
                  {t('screens.myApplicationsCalendar.title', 'Kalendarz')}
                </Text>
              </XStack>
            </YStack>
          </LinearGradient>
        )}

        <ScrollView
          contentContainerStyle={{ paddingHorizontal: 16, paddingTop: 16, paddingBottom: 100 }}
          showsVerticalScrollIndicator={false}
        >
        {/* Upcoming Meeting Banner */}
        {upcomingMeeting && (
          <YStack
            backgroundColor="rgba(251, 191, 36, 0.15)"
            borderRadius={16}
            padding="$4"
            borderWidth={1}
            borderColor="rgba(251, 191, 36, 0.3)"
            marginBottom="$4"
          >
            <Text fontSize={12} fontFamily="Poppins" color={COLORS.WARNING} fontWeight="500">
              {t('screens.myApplicationsCalendar.upcomingMeeting', 'Nadchodzące spotkanie')}
            </Text>
            <Text fontSize={14} fontFamily="Poppins" color={titleColor} marginTop="$1">
              {formatUpcomingDate(upcomingMeeting.date)} - {upcomingMeeting.title} ({t('screens.myApplicationsCalendar.reminderAt', 'przypomnienie o')} {upcomingMeeting.reminder})
            </Text>
          </YStack>
        )}

        {/* Calendar Card */}
        <YStack
          backgroundColor={cardBg}
          borderRadius={16}
          padding="$4"
          borderWidth={1}
          borderColor={softBorder}
          marginBottom="$4"
        >
          {/* Month Navigation */}
          <XStack justifyContent="space-between" alignItems="center" marginBottom="$4">
            <TouchableOpacity onPress={goToPrevMonth}>
              <ChevronLeft size={24} color={subtitleColor} />
            </TouchableOpacity>
            <Text fontSize={16} fontWeight="600" fontFamily="Poppins" color={titleColor}>
              {MONTHS_PL[calendarMonth]} ▾ {calendarYear}
            </Text>
            <TouchableOpacity onPress={goToNextMonth}>
              <ChevronRight size={24} color={subtitleColor} />
            </TouchableOpacity>
          </XStack>

          {/* Days of Week Header */}
          <XStack justifyContent="space-around" marginBottom="$2">
            {DAYS_PL.map((day) => (
              <YStack key={day} width={36} alignItems="center">
                <Text fontSize={10} fontWeight="500" fontFamily="Poppins" color={subtitleColor}>
                  {day}
                </Text>
              </YStack>
            ))}
          </XStack>

          {/* Calendar Grid */}
          <YStack>
            {Array.from({ length: Math.ceil(calendarDays.length / 7) }).map((_, weekIndex) => {
              const weekDays = calendarDays.slice(weekIndex * 7, (weekIndex + 1) * 7);
              // Fill the last row with empty cells if needed
              const paddedWeekDays = weekDays.length < 7 
                ? [...weekDays, ...Array(7 - weekDays.length).fill(null)]
                : weekDays;
              
              return (
                <XStack key={weekIndex} justifyContent="space-around" marginBottom="$2">
                  {paddedWeekDays.map((day, dayIndex) => {
                    const dayHasMeeting = day ? hasMeeting(day) : false;
                    const dayIsToday = day ? isToday(day) : false;

                    return (
                      <TouchableOpacity
                        key={dayIndex}
                        disabled={!day}
                        onPress={() => day && setSelectedDate(new Date(calendarYear, calendarMonth, day))}
                      >
                        <YStack
                          width={36}
                          height={36}
                          borderRadius={18}
                          alignItems="center"
                          justifyContent="center"
                          backgroundColor={
                            dayIsToday
                              ? COLORS.PRIMARY
                              : dayHasMeeting
                              ? 'rgba(239, 68, 68, 0.15)'
                              : 'transparent'
                          }
                          borderWidth={dayHasMeeting && !dayIsToday ? 2 : 0}
                          borderColor={COLORS.ERROR}
                        >
                          <Text
                            fontSize={14}
                            fontFamily="Poppins"
                            fontWeight={dayIsToday || dayHasMeeting ? '600' : '400'}
                            color={
                              dayIsToday
                              ? 'white'
                              : dayHasMeeting
                              ? COLORS.ERROR
                              : day
                              ? titleColor
                              : 'transparent'
                          }
                        >
                          {day || ''}
                        </Text>
                      </YStack>
                    </TouchableOpacity>
                  );
                })}
              </XStack>
            );
          })}
        </YStack>
        </YStack>

        {/* Notification Settings Card */}
        <YStack
          backgroundColor={cardBg}
          borderRadius={16}
          padding="$4"
          borderWidth={1}
          borderColor={softBorder}
        >
          <Text fontSize={16} fontWeight="600" fontFamily="Poppins" color={titleColor} marginBottom="$3">
            {t('screens.myApplicationsCalendar.notificationSettings', 'Ustawienia powiadomień')}
          </Text>

          {renderSettingRow(
            <Bell size={20} color={COLORS.PRIMARY} />,
            t('screens.myApplicationsCalendar.pushNotifications', 'Powiadomienia push'),
            t('screens.myApplicationsCalendar.pushNotificationsDesc', 'Otrzymuj powiadomienia na telefon'),
            pushNotifications,
            (val) => handleToggle('push', val)
          )}

          {renderSettingRow(
            <Mail size={20} color={COLORS.PRIMARY} />,
            t('screens.myApplicationsCalendar.emailReminders', 'Przypomnienia e-mail'),
            t('screens.myApplicationsCalendar.emailRemindersDesc', 'Wysyłaj przypomnienia na maila'),
            emailReminders,
            (val) => handleToggle('email', val)
          )}

          {renderSettingRow(
            <Volume2 size={20} color={COLORS.PRIMARY} />,
            t('screens.myApplicationsCalendar.sound', 'Dźwięk powiadomień'),
            t('screens.myApplicationsCalendar.soundDesc', 'Odtwarzaj dźwięk przy powiadomieniach'),
            soundEnabled,
            (val) => handleToggle('sound', val)
          )}

          <XStack
            paddingVertical="$3"
            alignItems="center"
          >
            <YStack
              width={40}
              height={40}
              borderRadius={20}
              backgroundColor={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}
              alignItems="center"
              justifyContent="center"
              marginRight="$3"
            >
              <Clock size={20} color={COLORS.PRIMARY} />
            </YStack>
            <YStack flex={1}>
              <Text fontSize={14} fontWeight="500" fontFamily="Poppins" color={titleColor}>
                {t('screens.myApplicationsCalendar.autoReminders', 'Automatyczne przypomnienia')}
              </Text>
              <Text fontSize={12} fontFamily="Poppins" color={subtitleColor}>
                {t('screens.myApplicationsCalendar.autoRemindersDesc', 'Ustaw domyślnie dla nowych spotkań')}
              </Text>
            </YStack>
            <Switch
              value={autoReminders}
              onValueChange={(val) => handleToggle('auto', val)}
              trackColor={{ false: '#767577', true: COLORS.PRIMARY }}
              thumbColor={autoReminders ? '#ffffff' : '#f4f3f4'}
            />
          </XStack>
        </YStack>

        {/* TODO: API - Endpoints needed:
            - GET /api/users/me/notification-settings
            - PUT /api/users/me/notification-settings
        */}
        </ScrollView>
      </YStack>
    </Screen>
  );
};

export default MyApplicationsCalendarScreen;


